/**
 * Lógica del registro del service worker, sin tocar el store ni `window` directamente: todo entra por
 * `PwaDeps`, así se puede probar con un Workbox falso. `registerSW.ts` la conecta con la app real.
 */

/** Cada cuánto se busca una versión nueva del service worker. */
export const UPDATE_CHECK_MS = 60 * 60 * 1000;

export interface LifecycleEventLike {
  /** `true` cuando el SW que toma el control reemplaza a uno anterior (no es la primera instalación). */
  isUpdate?: boolean;
}

/** Subconjunto de `Workbox` (workbox-window) que usa la app. */
export interface WorkboxLike {
  addEventListener(
    type: 'waiting' | 'controlling',
    listener: (event: LifecycleEventLike) => void,
  ): void;
  register(): Promise<ServiceWorkerRegistration | undefined>;
  messageSkipWaiting(): void;
}

export interface PwaDeps {
  createWorkbox: () => WorkboxLike;
  setUpdateAvailable: (value: boolean) => void;
  reload: () => void;
  isOnline: () => boolean;
  setInterval: (callback: () => void, ms: number) => unknown;
}

export interface PwaController {
  init(): Promise<void>;
  /** Pide al SW en espera que tome el control; la página se recarga sola cuando lo hace. */
  applyUpdate(): void;
}

export function createPwaController(deps: PwaDeps): PwaController {
  let workbox: WorkboxLike | null = null;

  async function init(): Promise<void> {
    if (workbox) return;
    const wb = deps.createWorkbox();
    workbox = wb;

    wb.addEventListener('waiting', () => deps.setUpdateAvailable(true));
    // En la primera instalación el SW también toma el control (clientsClaim): ahí NO se recarga.
    wb.addEventListener('controlling', (event) => {
      if (event.isUpdate) deps.reload();
    });

    const registration = await wb.register();
    if (!registration) return;
    deps.setInterval(() => {
      if (deps.isOnline()) registration.update().catch(() => undefined);
    }, UPDATE_CHECK_MS);
  }

  function applyUpdate(): void {
    workbox?.messageSkipWaiting();
  }

  return { init, applyUpdate };
}
